import axios from 'axios';
import { store } from '../store';
import ApiService from './api-service';
import config from '../config';
import { Channels } from '../util/constants';
import download from '../util/download';

const TemplateService = {
  async createTemplate(
    shortCode,
    name,
    { subject, text, attachInvoice, attachStatement, isMulti, channel } = {},
  ) {
    const body = {
      query: `mutation createTemplate(
          $name: String!,
          $subject: String,
          $text: String,
          $attachInvoice: Boolean,
          $attachStatement: Boolean,
          $isMulti: Boolean,
          $channel: String
        ) {
        createTemplate(
          name: $name,
          subject: $subject,
          text: $text,
          attachInvoice: $attachInvoice,
          attachStatement: $attachStatement,
          isMulti: $isMulti
          channel: $channel
        ) {
          templates {
            name
          }
        }
      }`,
      variables: { name, subject, text, attachInvoice, attachStatement, isMulti, channel },
    };
    const result = await ApiService.orgEndpoint(shortCode).post('/', body);
    return result.createTemplate;
  },

  async deleteTemplate(shortCode, name) {
    const body = {
      query: `mutation deleteTemplate($name: String!) {
        deleteTemplate(name: $name)
      }`,
      variables: { name },
    };

    const result = await ApiService.orgEndpoint(shortCode).post('/', body);
    return result.deleteTemplate;
  },

  async duplicateTemplate(shortCode, baseTemplateName, { name }) {
    const body = {
      query: `mutation duplicateTemplate($name: String!, $baseTemplateName: String!) {
        duplicateTemplate(name: $name, baseTemplateName: $baseTemplateName) {
          templates {
            name
          }
        }
      }`,
      variables: { name, baseTemplateName },
    };

    const result = await ApiService.orgEndpoint(shortCode).post('/', body);
    return result.duplicateTemplate;
  },

  async editTemplate(
    shortCode,
    templateId,
    { name, subject, text, attachInvoice, attachStatement, channel, dateFormat },
  ) {
    const body = {
      query: `mutation editTemplate(
        $templateId: String!,
        $name: String,
        $subject: String,
        $text: String,
        $attachInvoice: Boolean,
        $attachStatement: Boolean,
        $channel: String,
        $dateFormat: String
        ) {
        editTemplate(
          templateId: $templateId,
          name: $name,
          subject: $subject,
          text: $text,
          attachInvoice: $attachInvoice,
          attachStatement: $attachStatement,
          channel: $channel,
          dateFormat: $dateFormat
        ) {
          templates {
            name
          }
        }
      }`,
      variables: { templateId, name, subject, text, attachInvoice, attachStatement, dateFormat },
    };

    const result = await ApiService.orgEndpoint(shortCode).post('/', body);
    return result.editTemplate;
  },

  async getPreview(shortCode, subject, text, channel, dateFormat) {
    const body = {
      query: `query ($subject: String!, $text: String!, $channel: String, $dateFormat: String) {
        organisation {
          unsavedTemplatePreview(subject: $subject, text: $text, channel: $channel, dateFormat: $dateFormat) {
            subject
            text
            pdf
          }
        }
      }
      `,
      variables: {
        subject,
        text,
        channel,
        dateFormat,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);

    if (channel !== Channels.LETTER) {
      data.organisation.unsavedTemplatePreview.text
        .replaceAll('[Payment Portal Link]', '<a href="javascript:void(0)">Payment portal link</a>')
        .replaceAll(
          '[Online Invoice Link]',
          '<a href="javascript:void(0)">Online invoice link</a>',
        );
    }

    return data.organisation.unsavedTemplatePreview;
  },

  async sendTestMail(shortCode, subject, text) {
    const body = {
      query: `mutation ($subject: String!, $text: String!) {
        sendTemplatePreviewEmail(subject: $subject, html: $text)
      }
      `,
      variables: {
        subject,
        text,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    if (!data.sendTemplatePreviewEmail) throw new Error('Failed to send test email');
    return true;
  },

  async createStatementTemplate(shortCode, name, { subject, text } = {}) {
    const body = {
      query: `mutation createStatementTemplate($name: String!, $subject: String, $text: String) {
        createStatementTemplate(
          name: $name,
          subject: $subject,
          text: $text
        ) {
          statementTemplates {
            name
            channel
          }
        }
      }`,
      variables: { name, subject, text },
    };
    const result = await ApiService.orgEndpoint(shortCode).post('/', body);
    return result.createStatementTemplate;
  },

  async duplicateStatementTemplate(shortCode, baseTemplateName, { name }) {
    const body = {
      query: `mutation duplicateStatementTemplate($name: String!, $baseTemplateName: String!) {
        duplicateStatementTemplate(name: $name, baseTemplateName: $baseTemplateName) {
          templates {
            name
          }
        }
      }`,
      variables: { name, baseTemplateName },
    };
    const result = await ApiService.orgEndpoint(shortCode).post('/', body);
    return result.duplicateStatementTemplate;
  },

  async deleteStatementTemplate(shortCode, name) {
    const body = {
      query: `mutation deleteStatementTemplate($name: String!) {
        deleteStatementTemplate(name: $name)
      }`,
      variables: { name },
    };
    const result = await ApiService.orgEndpoint(shortCode).post('/', body);
    return result.deleteStatementTemplate;
  },

  async editStatementTemplate(shortCode, templateId, { name, subject, text }) {
    const body = {
      query: `mutation editStatementTemplate($templateId: String!, $name: String, $subject: String, $text: String) {
        editStatementTemplate(
          templateId: $templateId,
          name: $name,
          subject: $subject,
          text: $text,
        ) {
          templates {
            name
          }
        }
      }`,
      variables: { templateId, name, subject, text },
    };
    const result = await ApiService.orgEndpoint(shortCode).post('/', body);
    return result.editStatementTemplate;
  },

  async getTemplatePreviews(shortCode) {
    const body = {
      query: `query {
        organisation {
          ShortCode
          templates {
            name
            preview {
              subject
              text
            }
            isMulti
            channel
          }
          statementTemplates {
            name
            channel
          }
        }
      }
      `,
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.organisation;
  },

  async getTemplates({ shortCode }) {
    const body = {
      query: `query {
        organisation {
          templates {
            name
            subject
            text
            preview {
              subject
              text
            }
            isMulti
            attachInvoice
            attachStatement
            dateFormat
            schedules {
              _id
              name
            }
            channel
          }
          statementTemplates {
            name
            subject
            text
            preview {
              subject
              text
            }
            channel
            schedules {
              _id
              name
              monthlyStatement {
                active
              }
            }
          }
        }
      }
      `,
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.organisation;
  },

  async getStatementPreview(shortCode, subject, text) {
    const body = {
      query: `query ($subject: String!, $text: String!) {
        organisation {
          unsavedStatementTemplatePreview(subject: $subject, text: $text) {
            subject
            text
          }
        }
      }
      `,
      variables: {
        subject,
        text,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.organisation.unsavedStatementTemplatePreview;
  },

  async sendStatementTestMail(shortCode, subject, text) {
    const body = {
      query: `mutation ($subject: String!, $text: String!) {
        sendStatementTemplatePreviewEmail(subject: $subject, html: $text)
      }
      `,
      variables: {
        subject,
        text,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    if (!data.sendStatementTemplatePreviewEmail) throw new Error('Failed to send test email');
    return true;
  },

  async getAudioTemplatePreview(script) {
    const state = store.getState();
    const { token } = state.loginReducer;

    const { data } = await axios
      .create({
        baseURL: config.apiUrl,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      })
      .post(`${config.apiUrl}/textToSpeech/preview`, {
        script,
        voiceLanguageCode: 'en-GB',
        voiceName: 'en-GB-Standard-A',
      });

    return data;
  },
  async downloadLetterFromHTML(ShortCode, htmlContent) {
    const body = {
      query: `mutation($htmlContent: String!) {
        getLetterPdf(htmlContent: $htmlContent)
      }`,
      variables: {
        htmlContent,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post('/', body);

    let decodedString = window.atob(data.getLetterPdf);
    let uint8Array = new Uint8Array(decodedString.length);
    for (let i = 0; i < decodedString.length; i++) {
      uint8Array[i] = decodedString.charCodeAt(i);
    }

    download(uint8Array, 'letter.pdf', 'application/pdf');
  },
  async downloadLetterPdfFromPDF(base64) {
    let decodedString = window.atob(base64);
    let uint8Array = new Uint8Array(decodedString.length);
    for (let i = 0; i < decodedString.length; i++) {
      uint8Array[i] = decodedString.charCodeAt(i);
    }

    download(uint8Array, 'letter.pdf', 'application/pdf');
  },
  async generateAITemplate({ messageId, invoices, placeholders }) {
    const state = store.getState();
    const { token } = state.loginReducer;

    const { data } = await axios
      .create({
        baseURL: config.apiUrl,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      .post(`${config.apiUrl}/auto-reply/${messageId}`, { invoices, placeholders });

    return data;
  },
  async generateAIInvoiceTemplate({ invoices, placeholders, channel }) {
    const state = store.getState();
    const { token } = state.loginReducer;

    const { data } = await axios
      .create({
        baseURL: config.apiUrl,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      .post(`${config.apiUrl}/ai/template`, {
        data: { invoices, placeholders, channel },
      });

    return data;
  },
};

export default TemplateService;
